import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import {
  Calendar,
  Clock,
  Phone,
  Video,
  User,
  Mail,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Loader
} from 'lucide-react';

const AppointmentBooking = ({ backendUrl, prefilledData = null, onSuccess = null }) => {
  const { t, i18n } = useTranslation('public');
  const [step, setStep] = useState(1); // 1: Type, 2: Admin, 3: Date/Créneau, 4: Infos, 5: Confirmation
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Données formulaire
  const [rdvType, setRdvType] = useState('telephone');
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [clientInfo, setClientInfo] = useState({
    first_name: prefilledData?.first_name || '',
    last_name: prefilledData?.last_name || '',
    email: prefilledData?.email || '',
    phone: prefilledData?.phone || '',
    notes: ''
  });

  // Données disponibilités
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const rdvTypes = [
    { id: 'telephone', labelKey: 'appointment.types.telephone', icon: Phone, descKey: 'appointment.types.telephoneDesc' },
    { id: 'visio', labelKey: 'appointment.types.visio', icon: Video, descKey: 'appointment.types.visioDesc' }
  ];

  const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  // Charger disponibilités pour une date
  const loadAvailability = async (date) => {
    setLoading(true);
    setError(null);
    
    try {
      const dateStr = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
      const params = new URLSearchParams({
        date: dateStr,
        type_rdv: rdvType
      });
      
      const response = await fetch(`${backendUrl}/api/client/appointments/check-availability?${params}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableAdmins(data.available_admins || []);
      } else {
        throw new Error('Erreur lors de la vérification des disponibilités');
      }
    } catch (err) {
      setError(err.message);
      setAvailableAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger disponibilités quand date sélectionnée
  useEffect(() => {
    if (selectedDate && step === 3) {
      loadAvailability(selectedDate);
    }
  }, [selectedDate, rdvType]);

  // Soumettre RDV
  const submitAppointment = async () => {
    logger.debug('🚀 submitAppointment appelé');
    logger.debug('📋 Données à envoyer:', {
      type_rdv: rdvType,
      admin_id: selectedAdmin?.admin_id,
      date: selectedDate?.toISOString().split('T')[0],
      heure_debut: selectedSlot?.heure_debut,
      heure_fin: selectedSlot?.heure_fin,
      client_first_name: clientInfo.first_name,
      client_last_name: clientInfo.last_name,
      client_email: clientInfo.email,
      client_phone: clientInfo.phone,
      notes: clientInfo.notes
    });
    
    setLoading(true);
    setError(null);

    try {
      const dateStr = `${selectedDate.getUTCFullYear()}-${String(selectedDate.getUTCMonth() + 1).padStart(2, '0')}-${String(selectedDate.getUTCDate()).padStart(2, '0')}`;
      
      const response = await fetch(`${backendUrl}/api/client/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type_rdv: rdvType,
          admin_id: selectedAdmin?.admin_id,
          date: dateStr,
          heure_debut: selectedSlot.heure_debut,
          heure_fin: selectedSlot.heure_fin,
          client_first_name: clientInfo.first_name,
          client_last_name: clientInfo.last_name,
          client_email: clientInfo.email,
          client_phone: clientInfo.phone,
          notes: clientInfo.notes
        })
      });

      logger.debug('📡 Réponse reçue, status:', response.status);

      if (response.ok) {
        const data = await response.json();
        logger.debug('✅ RDV créé avec succès:', data);
        setStep(5);
        if (onSuccess) {
          setTimeout(() => onSuccess(), 2000);
        }
      } else {
        const data = await response.json();
        logger.error('❌ Erreur backend:', data);
        throw new Error(data.detail || 'Erreur lors de la création du rendez-vous');
      }
    } catch (err) {
      logger.error('❌ Erreur lors de la soumission:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Générer jours du mois (UTC pour éviter les problèmes de timezone)
  // Le calendrier commence le lundi (pas dimanche)
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(Date.UTC(year, month, 1));
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    const daysInMonth = lastDay.getUTCDate();
    // Convertir: 0=dimanche devient 6, 1=lundi devient 0, etc.
    const startingDayOfWeek = (firstDay.getUTCDay() + 6) % 7;

    const days = [];
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(Date.UTC(year, month, -i));
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(Date.UTC(year, month, i)), isCurrentMonth: true });
    }
    return days;
  };

  const isToday = (date) => {
    const today = new Date();
    const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    const dateUTC = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    return todayUTC.getTime() === dateUTC.getTime();
  };

  const isPastDate = (date) => {
    const today = new Date();
    const todayUTC = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
    const dateUTC = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    return dateUTC < todayUTC;
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  // Get translated month name
  const getMonthName = (monthIndex) => {
    return t(`appointment.calendar.months.${monthKeys[monthIndex]}`);
  };

  // Get translated day name
  const getDayName = (dayKey) => {
    return t(`appointment.calendar.days.${dayKey}`);
  };

  // Format date based on locale
  const formatDate = (date, options) => {
    const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, options);
  };

  // Étape 1 : Choix type RDV
  if (step === 1) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-pink-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Calendar size={32} className="text-purple-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">{t('appointment.title')}</h2>
            <p className="text-slate-600">{t('appointment.chooseType')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {rdvTypes.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => {
                    setRdvType(type.id);
                    setStep(2);
                  }}
                  className={`p-6 border-2 rounded-xl transition-all hover:border-purple-400 hover:shadow-lg ${
                    rdvType === type.id ? 'border-purple-600 bg-purple-50' : 'border-slate-200'
                  }`}
                >
                  <Icon size={48} className={`mx-auto mb-4 ${rdvType === type.id ? 'text-purple-600' : 'text-slate-400'}`} />
                  <h3 className="font-bold text-slate-900 mb-2">{t(type.labelKey)}</h3>
                  <p className="text-sm text-slate-600">{t(type.descKey)}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Étape 2 : Choix admin (optionnel - skip pour l'instant)
  if (step === 2) {
    setStep(3);
  }

  // Étape 3 : Choix date et créneau
  if (step === 3) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setStep(1)}
              className="flex items-center space-x-2 text-slate-600 hover:text-slate-900"
            >
              <ChevronLeft size={20} />
              <span>{t('common.back')}</span>
            </button>
            <h2 className="text-2xl font-bold text-slate-900">{t('appointment.chooseDate')}</h2>
            <div className="w-20"></div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3 text-red-700">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calendrier */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => {
                    const newDate = new Date(currentMonth);
                    newDate.setMonth(newDate.getMonth() - 1);
                    setCurrentMonth(newDate);
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronLeft size={20} />
                </button>
                <h3 className="text-lg font-bold">
                  {getMonthName(currentMonth.getMonth())} {currentMonth.getFullYear()}
                </h3>
                <button
                  onClick={() => {
                    const newDate = new Date(currentMonth);
                    newDate.setMonth(newDate.getMonth() + 1);
                    setCurrentMonth(newDate);
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayKeys.map(dayKey => (
                  <div key={dayKey} className="text-center text-xs font-medium text-slate-500 py-2">
                    {getDayName(dayKey)}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth(currentMonth).map((day, index) => (
                  <button
                    key={index}
                    disabled={!day.isCurrentMonth || isPastDate(day.date)}
                    onClick={() => setSelectedDate(day.date)}
                    className={`
                      aspect-square p-2 text-sm rounded-lg transition-all
                      ${!day.isCurrentMonth ? 'text-slate-300 cursor-default' : ''}
                      ${isPastDate(day.date) && day.isCurrentMonth ? 'text-slate-300 cursor-not-allowed' : ''}
                      ${day.isCurrentMonth && !isPastDate(day.date) ? 'hover:bg-purple-100 cursor-pointer' : ''}
                      ${isToday(day.date) ? 'bg-pink-100 font-bold text-pink-600' : ''}
                      ${selectedDate && day.date.getTime() === selectedDate.getTime() ? 'bg-purple-600 text-white font-bold' : ''}
                    `}
                  >
                    {day.date.getUTCDate()}
                  </button>
                ))}
              </div>
            </div>

            {/* Créneaux disponibles */}
            <div>
              {!selectedDate ? (
                <div className="text-center py-12 text-slate-400">
                  <Calendar size={48} className="mx-auto mb-3 opacity-30" />
                  <p>{t('appointment.calendar.selectDate')}</p>
                </div>
              ) : loading ? (
                <div className="text-center py-12">
                  <Loader size={48} className="mx-auto mb-3 animate-spin text-purple-600" />
                  <p className="text-slate-600">{t('appointment.calendar.searchingSlots')}</p>
                </div>
              ) : availableAdmins.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <AlertCircle size={48} className="mx-auto mb-3 opacity-30" />
                  <p className="font-medium mb-2">{t('appointment.calendar.noSlotAvailable')}</p>
                  <p className="text-sm">{t('appointment.calendar.tryOtherDate')}</p>
                </div>
              ) : (
                <div>
                  <h3 className="font-bold text-slate-900 mb-4">
                    {t('appointment.calendar.availableSlotsFor')} - {formatDate(selectedDate, { weekday: 'long', day: 'numeric', month: 'long' })}
                  </h3>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {availableAdmins.map((admin) => (
                      <div key={admin.admin_id} className="border border-slate-200 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-3">
                          <User size={16} className="text-slate-500" />
                          <span className="font-medium text-slate-900">{admin.admin_name}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {admin.slots.map((slot, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                setSelectedAdmin(admin);
                                setSelectedSlot(slot);
                                setStep(4);
                              }}
                              className="px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium"
                            >
                              {slot.heure_debut}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Étape 4 : Informations client
  if (step === 4) {
    const RdvIcon = rdvTypes.find(t => t.id === rdvType)?.icon || Phone;

    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setStep(3)}
              className="flex items-center space-x-2 text-slate-600 hover:text-slate-900"
            >
              <ChevronLeft size={20} />
              <span>{t('common.back')}</span>
            </button>
            <h2 className="text-2xl font-bold text-slate-900">{t('appointment.yourInfo')}</h2>
            <div className="w-20"></div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3 text-red-700">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Récapitulatif RDV */}
          <div className="mb-8 p-4 bg-purple-50 border border-purple-200 rounded-xl">
            <h3 className="font-bold text-slate-900 mb-3">{t('appointment.summary.title')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2">
                <RdvIcon size={16} className="text-purple-600" />
                <span className="text-slate-700">{t('appointment.summary.type')} : <strong>{t(rdvTypes.find(tp => tp.id === rdvType)?.labelKey)}</strong></span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar size={16} className="text-purple-600" />
                <span className="text-slate-700">{t('appointment.summary.date')} : <strong>{formatDate(selectedDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong></span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock size={16} className="text-purple-600" />
                <span className="text-slate-700">{t('appointment.summary.time')} : <strong>{selectedSlot.heure_debut} - {selectedSlot.heure_fin}</strong></span>
              </div>
              <div className="flex items-center space-x-2">
                <User size={16} className="text-purple-600" />
                <span className="text-slate-700">{t('appointment.summary.with')} : <strong>{selectedAdmin.admin_name}</strong></span>
              </div>
            </div>
          </div>

          {/* Formulaire */}
          <form onSubmit={(e) => { e.preventDefault(); submitAppointment(); }} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('appointment.form.firstName')} *</label>
                <input
                  type="text"
                  required
                  value={clientInfo.first_name}
                  onChange={(e) => setClientInfo({ ...clientInfo, first_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Jean"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('appointment.form.lastName')} *</label>
                <input
                  type="text"
                  required
                  value={clientInfo.last_name}
                  onChange={(e) => setClientInfo({ ...clientInfo, last_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Dupont"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('appointment.form.email')} *</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  required
                  value={clientInfo.email}
                  onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="jean.dupont@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('appointment.form.phone')} *</label>
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="tel"
                  required
                  value={clientInfo.phone}
                  onChange={(e) => setClientInfo({ ...clientInfo, phone: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="06 12 34 56 78"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('appointment.form.reason')}</label>
              <div className="relative">
                <MessageSquare size={18} className="absolute left-3 top-3 text-slate-400" />
                <textarea
                  value={clientInfo.notes}
                  onChange={(e) => setClientInfo({ ...clientInfo, notes: e.target.value })}
                  rows={3}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder={t('appointment.form.reasonPlaceholder')}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  <span>{t('common.loading')}</span>
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  <span>{t('appointment.confirmAppointment')}</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Étape 5 : Confirmation
  if (step === 5) {
    const RdvIcon = rdvTypes.find(tp => tp.id === rdvType)?.icon || Phone;

    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} className="text-emerald-600" />
          </div>
          
          <h2 className="text-3xl font-bold text-slate-900 mb-4">{t('appointment.confirmation.title')}</h2>
          <p className="text-lg text-slate-600 mb-8">{t('appointment.confirmation.message')}</p>

          <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-8 text-left">
            <h3 className="font-bold text-slate-900 mb-4 text-center">{t('appointment.confirmation.details')}</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <RdvIcon size={20} className="text-purple-600" />
                <span className="text-slate-700"><strong>{t('appointment.summary.type')} :</strong> {t(rdvTypes.find(tp => tp.id === rdvType)?.labelKey)}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Calendar size={20} className="text-purple-600" />
                <span className="text-slate-700">
                  <strong>{t('appointment.summary.date')} :</strong> {formatDate(selectedDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Clock size={20} className="text-purple-600" />
                <span className="text-slate-700"><strong>{t('appointment.summary.time')} :</strong> {selectedSlot.heure_debut} - {selectedSlot.heure_fin}</span>
              </div>
              <div className="flex items-center space-x-3">
                <User size={20} className="text-purple-600" />
                <span className="text-slate-700"><strong>{t('appointment.summary.with')} :</strong> {selectedAdmin.admin_name}</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-blue-700">
              <strong>{t('appointment.confirmation.emailSent')}</strong><br />
              {clientInfo.email}
            </p>
          </div>

          <button
            onClick={() => {
              setStep(1);
              setSelectedDate(null);
              setSelectedSlot(null);
              setSelectedAdmin(null);
              setClientInfo({ first_name: '', last_name: '', email: '', phone: '', notes: '' });
            }}
            className="px-6 py-3 bg-slate-200 text-slate-700 rounded-xl hover:bg-slate-300 transition-colors"
          >
            {t('appointment.confirmation.newAppointment')}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default AppointmentBooking;
